import { useCallback } from 'react'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { useIFINotificationsContext } from '../../IFINotificationsContext'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import {
  FrequencyDefault,
  FrequencyItem,
  IFINotificationEventItem,
  Permission,
} from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import { FrequencyInput, useEditIfiNotificationMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleEditNotification } from 'hooks/notificationHandler'
import RenderFormItems from './components/RenderFormItems'
import { errorHandler } from 'hooks/errorHandler'
import moment from 'moment'
import { IFINotificationFormItemValues } from './components/RenderFormItems/types'

export function useEdit() {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { recipients, refetch, notifications } = useIFINotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useEditIfiNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editIFINotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<
    EditNotificationEventOptionsFn<
      IFINotificationFormItemValues,
      IFINotificationEventItem
    >
  >(
    (event) => {
      const {
        every,
        asphaltLot,
        concreteTest,
        dailyInspectionControlPoint,
        dailyInspectionInspection,
        dailyInspectionPunchList,
        densityLot,
        densityTest,
      } = recipients(event)
      const initialFrequency = notifications?.find(
        (notification) => notification.notificationEventId === event.id
      )?.frequency
      let time
      if (initialFrequency) {
        if (
          initialFrequency.__typename === FrequencyItem.MinuteFrequencyItem.type
        )
          time = moment(initialFrequency?.minutes, 'mm')
        else if (
          initialFrequency.__typename === FrequencyItem.HourlyFrequencyItem.type
        )
          time = moment(initialFrequency?.hours, 'h')
        else if (
          initialFrequency.__typename === FrequencyItem.DailyFrequencyItem.type
        )
          time = moment(
            `${initialFrequency?.hour}:${initialFrequency?.minute}`,
            'h:mm a'
          )
        else if (
          initialFrequency.__typename === FrequencyItem.WeeklyFrequencyItem.type
        )
          time = moment(
            `${initialFrequency?.hour}:${initialFrequency?.minute}`,
            'h:mm a'
          )
      }

      return {
        permission: Permission.EditIFINotification,
        initialValues: {
          every: toLabeledValues(every),
          asphaltLot: toLabeledValues(asphaltLot),
          concreteTest: toLabeledValues(concreteTest),
          dailyInspectionControlPoint: toLabeledValues(
            dailyInspectionControlPoint
          ),
          dailyInspectionInspection: toLabeledValues(dailyInspectionInspection),
          dailyInspectionPunchList: toLabeledValues(dailyInspectionPunchList),
          densityLot: toLabeledValues(densityLot),
          densityTest: toLabeledValues(densityTest),
          frequencyType: initialFrequency?.__typename,
          day:
            initialFrequency?.__typename === 'WeeklyFrequency'
              ? initialFrequency.day
              : undefined,
          time,
        },
        onClear: async () => {
          if (!projectId) return errorHandler('projectId is not defined')
          await edit({
            variables: {
              input: {
                projectId,
                notificationEventId: event.id,
                every: { userIds: [], nonUserEmailIds: [] },
                asphaltLot: { userIds: [], nonUserEmailIds: [] },
                densityLot: { userIds: [], nonUserEmailIds: [] },
                densityTest: { userIds: [], nonUserEmailIds: [] },
                dailyInspectionControlPoint: {
                  userIds: [],
                  nonUserEmailIds: [],
                },
                dailyInspectionInspection: { userIds: [], nonUserEmailIds: [] },
                dailyInspectionPunchList: { userIds: [], nonUserEmailIds: [] },
                concreteTest: { userIds: [], nonUserEmailIds: [] },
                frequency: null,
              },
            },
          })
        },
        onSave: async ({ every, types, time, day, frequencyType }) => {
          if (!projectId) return errorHandler('projectId is not defined')
          let frequency: FrequencyInput | null = null
          if (frequencyType) {
            if (frequencyType === FrequencyItem.MinuteFrequencyItem.type)
              frequency = {
                minute: {
                  minutes: time?.minutes() ?? FrequencyDefault.MINUTES,
                },
              }
            else if (frequencyType === FrequencyItem.HourlyFrequencyItem.type)
              frequency = {
                hourly: {
                  hours: time?.hour() ?? FrequencyDefault.HOURS,
                },
              }
            else if (frequencyType === FrequencyItem.DailyFrequencyItem.type)
              frequency = {
                daily: {
                  hour: time?.hour() ?? FrequencyDefault.HOUR,
                  minute: time?.minutes() ?? FrequencyDefault.MINUTE,
                },
              }
            else if (frequencyType === FrequencyItem.WeeklyFrequencyItem.type)
              frequency = {
                weekly: {
                  day: day ?? FrequencyDefault.DAY,
                  hour: time?.hour() ?? FrequencyDefault.HOUR,
                  minute: time?.minutes() ?? FrequencyDefault.MINUTE,
                },
              }
          }
          await edit({
            variables: {
              input: {
                projectId,
                notificationEventId: event.id,
                every: fromLabeledValues(every),
                asphaltLot: fromLabeledValues(
                  types?.find((type) => type.classType === 'AsphaltLot')
                    ?.recipients
                ),
                densityLot: fromLabeledValues(
                  types?.find((type) => type.classType === 'Lot')?.recipients
                ),
                densityTest: fromLabeledValues(
                  types?.find((type) => type.classType === 'DensityTest')
                    ?.recipients
                ),
                dailyInspectionControlPoint: fromLabeledValues(
                  types?.find(
                    (type) => type.classType === 'DailyInspectionControlPoint'
                  )?.recipients
                ),
                dailyInspectionInspection: fromLabeledValues(
                  types?.find(
                    (type) => type.classType === 'DailyInspectionInspection'
                  )?.recipients
                ),
                dailyInspectionPunchList: fromLabeledValues(
                  types?.find(
                    (type) => type.classType === 'DailyInspectionPunchList'
                  )?.recipients
                ),
                concreteTest: fromLabeledValues(
                  types?.find((type) => type.classType === 'ConcreteTest')
                    ?.recipients
                ),
                frequency,
              },
            },
          })
          refetch()
        },
        render: <RenderFormItems />,
      }
    },
    [
      edit,
      fromLabeledValues,
      projectId,
      recipients,
      refetch,
      toLabeledValues,
      notifications,
    ]
  )
}
